import { Header4 } from "@dash/page";
import { Skeleton } from "@dash/skeleton";
import { RowCellContent, Table, TableBody, TableHeadColumn, TableRowProps } from "@dash/table";
import { CSSObject, useTheme } from "@emotion/react";
import React from "react";

import { formatValue, getCellValue } from "./diffValues";
import { ScrollableTableFrame } from "./ScrollableTableFrame";
import { DiffSyncRecord, ValueFormatter } from "./types";

export const DiffSyncErrorsTable = <T extends Record<string, unknown>>(props: {
  diffs: DiffSyncRecord<T>[] | undefined;
  labels?: Partial<Record<keyof T & string, string>>;
  formatters?: Partial<Record<keyof T & string, ValueFormatter>>;
  alwaysVisibleColumns?: readonly (keyof T & string)[];
  isLoading?: boolean;
}) => {
  const theme = useTheme();

  if (!props.diffs) return props.isLoading ? <Skeleton /> : null;
  if (props.diffs.length === 0) return null;

  const contextColumns = props.alwaysVisibleColumns ?? [];
  const columns: TableHeadColumn<string>[] = [
    { label: "Id" },
    { label: "Błąd" },
    ...contextColumns.map((key) => ({ label: props.labels?.[key] ?? key })),
  ];

  const errorCss: CSSObject = {
    background: theme.colors.dangerBackground,
    color: theme.colors.danger,
    fontWeight: 700,
  };

  const rows: TableRowProps[] = props.diffs.map((diff) => {
    const source = diff.local ?? diff.remote;
    const baseline = diff.remote ?? diff.local;

    return {
      id: diff.diffId,
      cellsContent: [
        {
          element: diff.diffId,
          cellCss: errorCss,
        },
        {
          element: diff.error ?? "-",
          cellCss: errorCss,
        },
        ...contextColumns.map<RowCellContent>((key) => ({
          element: formatValue(getCellValue(source, baseline, key, false), props.formatters?.[key]),
        })),
      ],
    };
  });

  return (
    <div css={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
      <Header4>Błędy</Header4>
      <ScrollableTableFrame>
        <Table
          columns={columns}
          css={{
            whiteSpace: "nowrap",
          }}
        >
          <TableBody isLoading={props.isLoading} rows={rows} />
        </Table>
      </ScrollableTableFrame>
    </div>
  );
};
